import { type FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
} from "flowbite-react";
import { HiHome, HiRefresh, HiSearch } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { Pagination } from "../users/list";
import {
  getEmailMessageEvents,
  getEmailMessagesPage,
  type EmailAuditFilterSource,
  type EmailAuditFilterStatus,
  type EmailMessageEventRow,
  type EmailMessageRow,
} from "../../services/email-audit";
import { retryFailedContractEmail } from "../../services/contract-email-retry";
import { useNotification } from "../../contexts/NotificationContext";

const PAGE_SIZE = 20;
type BadgeColor =
  | "gray"
  | "info"
  | "success"
  | "warning"
  | "failure"
  | "purple"
  | "indigo";

const STATUS_OPTIONS: EmailAuditFilterStatus[] = [
  "all",
  "queued",
  "sent",
  "delivered",
  "delivery_delayed",
  "bounced",
  "complained",
  "opened",
  "clicked",
  "failed",
  "unknown",
];

const SOURCE_OPTIONS: EmailAuditFilterSource[] = [
  "all",
  "contract_api",
  "daily_report_resend",
  "process_report_queue",
  "reports_unknown",
  "unknown",
];

const STATUS_BADGE_COLORS: Record<string, BadgeColor> = {
  queued: "gray",
  sent: "info",
  delivered: "success",
  delivery_delayed: "warning",
  bounced: "failure",
  complained: "failure",
  opened: "purple",
  clicked: "indigo",
  failed: "failure",
  unknown: "gray",
};

const SOURCE_LABELS: Record<string, string> = {
  contract_api: "Contract API",
  daily_report_resend: "Daily Report",
  process_report_queue: "Report Queue",
  reports_unknown: "Reports (Webhook)",
  unknown: "Unknown",
};

const getDefaultDateRange = (): { from: string; to: string } => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - 30);
  return {
    from: fromDate.toISOString().slice(0, 10),
    to: toDate.toISOString().slice(0, 10),
  };
};

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("de-DE");
};

const formatJson = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "{}";
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    console.error("Error formatting JSON for email audit detail:", error);
    return "{}";
  }
};

const renderStatusBadge = (status: string) => {
  const color = STATUS_BADGE_COLORS[status] || "gray";
  return <Badge color={color}>{status}</Badge>;
};

const EmailAuditPage: FC = () => {
  const { showNotification } = useNotification();
  const requestIdRef = useRef(0);
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [rows, setRows] = useState<EmailMessageRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [retryingRowId, setRetryingRowId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] =
    useState<EmailAuditFilterSource>("all");
  const [statusFilter, setStatusFilter] =
    useState<EmailAuditFilterStatus>("all");
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);
  const [selectedRow, setSelectedRow] = useState<EmailMessageRow | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<EmailMessageEventRow[]>(
    []
  );
  const [eventsLoading, setEventsLoading] = useState(false);
  const closeDetailsModal = () => {
    setSelectedRow(null);
    setSelectedEvents([]);
    setEventsLoading(false);
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, sourceFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    const fetchPage = async () => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);

      const result = await getEmailMessagesPage({
        page: currentPage,
        pageSize: PAGE_SIZE,
        searchTerm: debouncedSearchTerm,
        source: sourceFilter,
        status: statusFilter,
        dateFrom,
        dateTo,
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      setRows(result.data);
      setTotalCount(result.total);
      setIsLoading(false);
    };

    void fetchPage();
  }, [
    currentPage,
    dateFrom,
    dateTo,
    debouncedSearchTerm,
    refreshNonce,
    sourceFilter,
    statusFilter,
  ]);

  const handleRetry = async (row: EmailMessageRow) => {
    if (retryingRowId !== null) {
      return;
    }

    setRetryingRowId(row.id);
    try {
      const result = await retryFailedContractEmail(row);
      if (result.success) {
        showNotification("E-Mail wurde erneut gesendet", "success");
      } else {
        showNotification(
          result.error ?? "E-Mail Retry fehlgeschlagen",
          "error"
        );
      }
      setRefreshNonce((value) => value + 1);
    } catch (error) {
      console.error("Email retry failed:", error);
      showNotification("E-Mail Retry fehlgeschlagen", "error");
    } finally {
      setRetryingRowId(null);
    }
  };

  const openDetails = async (row: EmailMessageRow) => {
    setSelectedRow(row);
    setSelectedEvents([]);

    if (!row.provider_message_id) {
      return;
    }

    setEventsLoading(true);
    const events = await getEmailMessageEvents(row.provider_message_id);
    setSelectedEvents(events);
    setEventsLoading(false);
  };

  return (
    <NavbarSidebarLayout isFooter={false} pageTitle="E-Mail Audit">
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="#">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Startseite</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/admin/email-audit">
                E-Mail Audit
              </BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              E-Mail Versandprotokoll
            </h1>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <TextInput
              id="email-audit-search"
              icon={HiSearch}
              placeholder="Empfänger, Betreff oder Message ID"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <Select
              id="email-audit-source"
              value={sourceFilter}
              onChange={(event) =>
                setSourceFilter(event.target.value as EmailAuditFilterSource)
              }
            >
              {SOURCE_OPTIONS.map((source) => (
                <option key={source} value={source}>
                  {source === "all" ? "Alle Quellen" : SOURCE_LABELS[source]}
                </option>
              ))}
            </Select>
            <Select
              id="email-audit-status"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as EmailAuditFilterStatus)
              }
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === "all" ? "Alle Status" : status}
                </option>
              ))}
            </Select>
            <TextInput
              id="email-audit-date-from"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
            <TextInput
              id="email-audit-date-to"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table hoverable>
          <TableHead>
            <TableHeadCell>Zeitpunkt</TableHeadCell>
            <TableHeadCell>Quelle</TableHeadCell>
            <TableHeadCell>Empfänger</TableHeadCell>
            <TableHeadCell>Betreff</TableHeadCell>
            <TableHeadCell>Status</TableHeadCell>
            <TableHeadCell>Provider ID</TableHeadCell>
            <TableHeadCell>Vertrag</TableHeadCell>
            <TableHeadCell>Versuche</TableHeadCell>
            <TableHeadCell>Fehler</TableHeadCell>
            <TableHeadCell>
              <span className="sr-only">Aktionen</span>
            </TableHeadCell>
          </TableHead>
          <TableBody className="divide-y">
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <div className="flex items-center justify-center py-12">
                    <Spinner size="xl" />
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Keine E-Mail-Einträge für die aktuellen Filter gefunden.
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell>
                    {SOURCE_LABELS[row.source] || row.source}
                  </TableCell>
                  <TableCell>{row.recipient_email}</TableCell>
                  <TableCell className="max-w-[220px] truncate">
                    {row.subject || "-"}
                  </TableCell>
                  <TableCell>{renderStatusBadge(row.status)}</TableCell>
                  <TableCell className="max-w-[220px] truncate font-mono text-xs">
                    {row.provider_message_id || "-"}
                  </TableCell>
                  <TableCell>
                    {row.contract_id ? (
                      <span>
                        #{row.contract_id}
                        {row.contract_type ? ` (${row.contract_type})` : ""}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{row.send_attempts ?? "-"}</TableCell>
                  <TableCell className="max-w-[260px] truncate text-sm">
                    {row.error_message
                      ? `${row.error_code ?? "-"}: ${row.error_message}`
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {row.status === "failed" &&
                        row.source === "contract_api" &&
                        row.contract_id !== null && (
                          <Button
                            color="blue"
                            size="xs"
                            disabled={retryingRowId !== null}
                            onClick={() => void handleRetry(row)}
                          >
                            {retryingRowId === row.id ? (
                              <Spinner size="xs" />
                            ) : (
                              <HiRefresh className="mr-1 h-3 w-3" />
                            )}
                            Retry
                          </Button>
                        )}
                      <Button
                        color="light"
                        size="xs"
                        onClick={() => void openDetails(row)}
                      >
                        Details
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        totalUsers={totalCount}
        currentPage={currentPage}
        usersPerPage={PAGE_SIZE}
        setCurrentPage={setCurrentPage}
      />

      <Modal
        show={selectedRow !== null}
        size="7xl"
        dismissible
        onClose={closeDetailsModal}
      >
        <ModalHeader>E-Mail Detailansicht</ModalHeader>
        <ModalBody>
          {selectedRow && (
            <div className="space-y-6 text-gray-900 dark:text-gray-100">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Erstellt
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDateTime(selectedRow.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Status
                  </p>
                  <div className="mt-1">
                    {renderStatusBadge(selectedRow.status)}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Quelle
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {SOURCE_LABELS[selectedRow.source] || selectedRow.source}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Empfänger
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedRow.recipient_email}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Sender
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedRow.sender_email || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Provider Message ID
                  </p>
                  <p className="font-mono text-xs text-gray-900 dark:text-gray-100">
                    {selectedRow.provider_message_id || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Versuche / Laufzeit
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedRow.send_attempts ?? "-"} /{" "}
                    {selectedRow.send_elapsed_ms ?? "-"} ms
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Letztes Event
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDateTime(selectedRow.last_event_at)}
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Fehler
                </p>
                <p className="rounded border border-gray-200 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                  {selectedRow.error_message
                    ? `${selectedRow.error_code ?? "-"}: ${
                        selectedRow.error_message
                      }`
                    : "Kein Fehler protokolliert"}
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                  Webhook Event Timeline
                </h3>
                {eventsLoading ? (
                  <div className="flex items-center gap-2">
                    <Spinner size="sm" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Events werden geladen...
                    </span>
                  </div>
                ) : selectedEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Keine Webhook-Events für diese Nachricht vorhanden.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-700">
                    <Table>
                      <TableHead>
                        <TableHeadCell>Event</TableHeadCell>
                        <TableHeadCell>Provider Zeit</TableHeadCell>
                        <TableHeadCell>Empfangen</TableHeadCell>
                      </TableHead>
                      <TableBody className="divide-y">
                        {selectedEvents.map((event) => (
                          <TableRow
                            key={`${event.id}_${event.provider_message_id}`}
                            className="bg-white dark:border-gray-700 dark:bg-gray-800"
                          >
                            <TableCell className="text-gray-900 dark:text-gray-100">
                              {event.event_type}
                            </TableCell>
                            <TableCell className="text-gray-900 dark:text-gray-100">
                              {formatDateTime(event.provider_created_at)}
                            </TableCell>
                            <TableCell className="text-gray-900 dark:text-gray-100">
                              {formatDateTime(event.received_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                  Metadata
                </h3>
                <pre className="max-h-64 overflow-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                  {formatJson(selectedRow.metadata)}
                </pre>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                  Initial Response
                </h3>
                <pre className="max-h-64 overflow-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                  {formatJson(selectedRow.initial_response)}
                </pre>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={closeDetailsModal}>
            Schließen
          </Button>
        </ModalFooter>
      </Modal>
    </NavbarSidebarLayout>
  );
};

export default EmailAuditPage;
